import {
  Dispatch,
  FC,
  FormEventHandler,
  RefObject,
  SetStateAction,
  SyntheticEvent,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ErrorDialog from 'components/organization/dialog/error-dialog';
import { useUnitApi } from 'hooks/use-unit-api';
import { getUnit } from 'services';
import { ActiveUnit } from 'types';
import EditUnitForm from './edit-unit-form';

type EditUnitContextType = {
  unit?: ActiveUnit | null;
  unitId: number;
  unitNumberRef: RefObject<HTMLInputElement>;
  handleReset: React.FormEventHandler<HTMLFormElement>;
  handleSubmit: React.FormEventHandler<HTMLFormElement>;
  setUnit: Dispatch<SetStateAction<ActiveUnit | null | undefined>>;
};

const EditUnitContext = createContext<EditUnitContextType>({
  unitId: 0,
  unitNumberRef: { current: null },
  handleReset: () => {},
  handleSubmit: () => {},
  setUnit: () => {}
});

export const useEditUnit = () => useContext(EditUnitContext);

const EditUnit: FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const unitId = useMemo(() => Number(params.unitId), [params.unitId]);

  const { updateUnit } = useUnitApi();

  const unitNumberRef = useRef<HTMLInputElement>(null);
  const [unit, setUnit] = useState<ActiveUnit | null>();

  useEffect(() => {
    if (unitId) getUnit(unitId).then(setUnit);
  }, [unitId, setUnit]);

  const handleReset = useCallback(() => {
    if (!unitId) navigate('/units', { replace: true });
    else navigate(`/units/${unitId}`, { replace: true });
  }, [unitId, navigate]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
      e.preventDefault();
      if (!unit) return;
      await updateUnit(unit.id, unit);
    },
    [unit, updateUnit]
  );

  return (
    <EditUnitContext.Provider value={{ unit, unitId, unitNumberRef, handleReset, handleSubmit, setUnit }}>
      <EditUnitForm />
      <ErrorDialog />
    </EditUnitContext.Provider>
  );
};

export default EditUnit;
